/**
 * Inline lesson exercise placeholder in the lesson content flow.
 * Learners tap to open the full exercise modal (same as end-of-lesson practice).
 */

import React, { useMemo } from 'react';
import { CheckCircle2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { ExerciseLockedInline } from '../shared/ExerciseLockedInline';
import { LessonExerciseHierarchyIcon } from '../admin/LessonExerciseHierarchyIcon';
import { SubscriptionPlanIcons } from '../shared/SubscriptionPlanIcons';
import { ClickableTooltip } from '../shared/ClickableTooltip';
import type { LessonExerciseSummaryDto } from '../../services/contentService';
import type { SubscriptionPlan } from '../../types/subscription';
import { useAppLocale } from '../../providers/LocaleProvider';
import { useLocalizedContent } from '../../hooks/useLocalizedContent';
import { useContentLocaleScope } from '../../hooks/useContentLocaleScope';
import { pickLocalizedDescription, pickLocalizedTitle } from '../../utils/localizedContent';
import { isDesignedForNonFarsiUsers } from '../../utils/nonFarsiContentHint';
import { PersianText } from '../shared/PersianText';
import { useElementMinWidth } from '../../hooks/useElementMinWidth';
import { useLearnerHomeDesign } from '../../utils/learnerHomeDesign';

const INLINE_EXERCISE_ROW_MIN_PX = 640;

export interface InlineLessonExerciseBlockProps {
  exerciseId: number;
  blockTitle?: string;
  /** Summary from lesson detail (subscription + completion). */
  exerciseSummary?: LessonExerciseSummaryDto;
  userPlanIds: number[];
  subscriptionPlans: SubscriptionPlan[];
  isHighlighted?: boolean;
  onOpen: (event: React.MouseEvent<HTMLButtonElement>) => void;
  /** Admin lesson preview: non-interactive card. */
  preview?: boolean;
}

export const InlineLessonExerciseBlock: React.FC<InlineLessonExerciseBlockProps> = ({
  exerciseId,
  blockTitle,
  exerciseSummary,
  userPlanIds,
  subscriptionPlans,
  isHighlighted = false,
  onOpen,
  preview = false,
}) => {
  const { t } = useTranslation('learning');
  const { direction } = useAppLocale();
  const [homeDesign] = useLearnerHomeDesign();
  const simpleHome = homeDesign === 'mme';
  const requiredPlanIds = exerciseSummary?.subscriptionPlanIds ?? [];
  const hasRequiredPlan =
    requiredPlanIds.length === 0 || requiredPlanIds.some((id) => userPlanIds.includes(id));

  const requiredPlans = useMemo(
    () =>
      requiredPlanIds
        .map((id) => subscriptionPlans.find((p) => p.id === id))
        .filter((p): p is SubscriptionPlan => !!p)
        .map((p) => ({
          id: p.id,
          name: p.name,
          ...(p.description != null && p.description !== '' ? { description: p.description } : {}),
        })),
    [requiredPlanIds, subscriptionPlans]
  );

  const exerciseForceEnglish = isDesignedForNonFarsiUsers(exerciseSummary);
  const { pickTitle: pickUiTitle, pickDescription: pickUiDescription } = useLocalizedContent({
    forceEnglish: exerciseForceEnglish,
  });
  const { tContent, contentLocaleAttrs } = useContentLocaleScope(exerciseForceEnglish, ['learning']);
  const tc = exerciseForceEnglish ? tContent : t;
  const isRtl = direction === 'rtl' && !exerciseForceEnglish;
  const cardRtl = exerciseForceEnglish ? false : isRtl;
  const cardDir = exerciseForceEnglish ? contentLocaleAttrs.dir : direction;

  const pickItemTitle = (item: { title?: string; titleFa?: string; isDesignedForNonFarsiUsers?: boolean }) =>
    item.isDesignedForNonFarsiUsers ? pickLocalizedTitle(item, 'en') : pickUiTitle(item);
  const pickItemDescription = (item: {
    description?: string;
    descriptionFa?: string;
    isDesignedForNonFarsiUsers?: boolean;
  }) =>
    item.isDesignedForNonFarsiUsers ? pickLocalizedDescription(item, 'en') : pickUiDescription(item);

  const highlightClass = isHighlighted ? 'search-result-highlight scroll-mt-24' : '';
  const exerciseTitle = exerciseSummary ? pickItemTitle(exerciseSummary) : '';
  const displayTitle =
    exerciseTitle ||
    blockTitle?.trim() ||
    tc('inlineExercise.defaultTitle', { defaultValue: 'Practice exercise' });
  const description = exerciseSummary ? pickItemDescription(exerciseSummary) : '';
  const completed = exerciseSummary?.userHasCompletedExercise === true;
  const questionCount = exerciseSummary?.questionCount ?? 0;
  const [rootRef, wide] = useElementMinWidth(INLINE_EXERCISE_ROW_MIN_PX);

  if (!preview && !hasRequiredPlan) {
    return (
      <div className={highlightClass}>
        <ExerciseLockedInline
          {...(displayTitle ? { exerciseTitle: displayTitle } : {})}
          requiredPlans={requiredPlans}
        />
      </div>
    );
  }

  return (
    <div
      ref={rootRef}
      className={`overflow-hidden rounded-2xl border border-amber-200/90 bg-gradient-to-br from-white via-amber-50/60 to-orange-50/30 shadow-sm dark:border-amber-800/45 dark:from-slate-900 dark:via-amber-950/20 dark:to-slate-950 ${highlightClass}`}
      {...(exerciseForceEnglish ? contentLocaleAttrs : {})}
    >
      <div
        className={`flex gap-3 ${
          wide ? 'flex-row items-center justify-between p-5' : 'flex-col p-4'
        }`}
      >
        <div
          className={`flex min-w-0 flex-1 gap-2 ${wide ? 'items-center' : 'items-start'} ${cardRtl ? 'flex-row-reverse' : ''}`}
          dir="ltr"
        >
          <div className={`flex shrink-0 items-center gap-1.5 ${cardRtl ? 'flex-row-reverse' : ''}`}>
            <ClickableTooltip tooltip={tc('inlineExercise.tooltipLabel', { defaultValue: 'Inline exercise' })}>
              <div className="inline-flex h-[2.25rem] w-[2.25rem] flex-shrink-0 items-center justify-center rounded-xl bg-amber-100/95 p-2 text-amber-800 dark:bg-amber-900/35 dark:text-amber-200">
                <LessonExerciseHierarchyIcon inline className="h-5 w-5" bare />
              </div>
            </ClickableTooltip>
            {!preview ? (
              <SubscriptionPlanIcons
                planIds={exerciseSummary?.subscriptionPlanIds ?? []}
                plans={subscriptionPlans}
                compact
              />
            ) : null}
          </div>
          <div className="min-w-0 flex-1 text-start" dir={cardDir}>
            <p className="text-xs font-semibold uppercase tracking-wide text-amber-800/90 dark:text-amber-300/90 mb-0.5">
              {tc('inlineExercise.sectionLabel', { defaultValue: 'Inline exercise' })}
            </p>
            <h3
              className={`flex items-center gap-1.5 font-semibold text-gray-900 dark:text-gray-100 ${
                cardRtl ? 'flex-row-reverse justify-end' : ''
              }`}
            >
              {completed && (
                <CheckCircle2
                  className="h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400"
                  aria-label={tc('exercise.passedAria')}
                />
              )}
              <span>
                {exerciseForceEnglish ? displayTitle : <PersianText>{displayTitle}</PersianText>}
              </span>
            </h3>
            {!simpleHome && description ? (
              <p className="mt-0.5 line-clamp-2 text-xs leading-snug text-gray-600 dark:text-gray-400">
                {exerciseForceEnglish ? description : <PersianText>{description}</PersianText>}
              </p>
            ) : !simpleHome ? (
              <p className="mt-0.5 text-sm text-gray-600 dark:text-gray-400">
                {tc('inlineExercise.openHint', {
                  defaultValue: 'Open this practice to answer questions at your own pace.',
                })}
              </p>
            ) : null}
            {questionCount > 0 ? (
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-500">
                {tc(
                  questionCount === 1 ? 'exercise.questionCount_one' : 'exercise.questionCount_other',
                  { count: questionCount }
                )}
                {exerciseSummary?.mandatoryForPassingLesson
                  ? tc('assessment.requiredToPassSuffix')
                  : ''}
              </p>
            ) : null}
          </div>
        </div>
        <div
          className={`flex shrink-0 flex-col items-stretch gap-1.5 ${
            wide ? (cardRtl ? 'items-start' : 'items-end') : ''
          }`}
        >
          {preview ? (
            <span className="rounded-lg border border-amber-300/80 bg-amber-50 px-4 py-2 text-sm font-medium text-amber-900 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-100">
              {tc('inlineExercise.previewCta', { defaultValue: 'Opens as exercise modal' })}
            </span>
          ) : (
            <button
              type="button"
              onClick={onOpen}
              className="rounded-lg bg-gradient-to-r from-amber-600 to-amber-700 px-4 py-2.5 text-sm font-semibold text-white shadow-md transition hover:from-amber-500 hover:to-amber-600"
              aria-label={tc('inlineExercise.openAria', {
                defaultValue: 'Open inline exercise',
                title: displayTitle,
              })}
              data-exercise-id={exerciseId}
            >
              {completed ? tc('exercise.practiceAgain') : tc('exercise.startExercise')}
            </button>
          )}
          {completed && !preview ? (
            <p
              className={`text-center text-xs font-medium text-emerald-700 dark:text-emerald-400 ${
                wide ? (cardRtl ? 'text-start' : 'text-end') : ''
              }`}
            >
              {tc('exercise.passedThisPractice')}
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
};
